import { Component, Input, OnInit, Output, EventEmitter, forwardRef } from "@angular/core";
import { RestApiService } from "src/app/core/services/rest-api.service";
import { environment } from "src/environments/environment";
import { BaseSelectComponent } from "../base/base-select";
import { FormControl, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Collection } from "src/app/core/const/collections";
import { withProjetHorsNegociation } from "src/app/core/const/projet-filters";

@Component({
	selector: "app-select-api",
	templateUrl: "select-api.component.html",
	styleUrl: "select-api.component.scss",
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SelectApiComponent),
			multi: true,
		},
	],
})
export class SelectApiComponent extends BaseSelectComponent implements OnInit {
	public items: any[];
	@Input() public collection: string = "";
	@Output() public selectItem = new EventEmitter();
	@Output() public onType = new EventEmitter();

	@Input() public filter = "";
	@Input() public populate: string = "";

	// Store the actual bindLabel to compute display
	private _bindLabelFields: string | string[];

	constructor(private restApiService: RestApiService) {
		super();
	}

	ngOnInit() {
		// Mode ngModel : aucun formControl fourni -> on en crée un interne et on
		// relaie ses changements vers le CVA (onChange) pour que ngModelChange se déclenche.
		if (!this.formControl) {
			this.formControl = new FormControl(this.value ?? null);
			this.formControl.valueChanges.subscribe((val) => {
				this.value = val;
				this.onChange(val);
			});
		}

		// Store original bindLabel for computing display
		this._bindLabelFields = this.bindLabel;
		
		// If bindLabel is array, use computed field
		if (Array.isArray(this.bindLabel)) {
			this.bindLabel = "_displayLabel";
		}

		this.restApiService.get(this.collection, { filters: this.effectiveFilter, populate: this.populate, pagination: { page: 0, pageSize: environment.pageSizeAll } }).subscribe((response) => {
			let data: any[];
			if (this.collection === Collection.UTILISATEUR) {
				data = response;
			} else {
				data = response.data;
			}
			this.items = this.processItems(data);
			this.ensureSelectedLoaded();
		});

		// La valeur peut arriver après le chargement (reset du formulaire en édition).
		this.formControl?.valueChanges?.subscribe(() => this.ensureSelectedLoaded());
	}

	/**
	 * Pour les projets, un filtre isActif=true implique aussi « hors négociation » :
	 * centralisé ici pour couvrir tous les sélecteurs de projet de l'application.
	 */
	private get effectiveFilter(): string {
		return this.collection === Collection.PROJET ? withProjetHorsNegociation(this.filter) : this.filter;
	}

	/**
	 * Garantit que la valeur actuellement sélectionnée fait partie des options,
	 * même si le filtre (ex. isActif=true) l'exclut. Sans ça, ng-select ne peut
	 * pas afficher le libellé d'une valeur existante (édition d'un enregistrement
	 * pointant vers un élément filtré) et le champ paraît vide.
	 */
	private ensureSelectedLoaded(): void {
		const key = this.bindValue || "id";
		const value = this.formControl ? this.formControl.value : this.value;
		const ids = Array.isArray(value) ? value : value != null ? [value] : [];
		// Seules des valeurs scalaires (id) peuvent etre chargees ; un objet (relation peuplee) est ignore
		const missing = ids.filter((id) => (typeof id === "string" || typeof id === "number") && !(this.items || []).some((it) => it[key] === id));
		if (!missing.length) return;

		this.restApiService
			.get(this.collection, { filters: `filters[${key}][$in]=${missing.join(",")}`, populate: this.populate, pagination: { page: 0, pageSize: environment.pageSizeAll } })
			.subscribe((response) => {
				const data: any[] = this.collection === Collection.UTILISATEUR ? response : response.data;
				if (data?.length) {
					this.items = [...this.processItems(data), ...(this.items || [])];
				}
			});
	}

	private processItems(items: any[]): any[] {
		if (!Array.isArray(this._bindLabelFields)) {
			return items;
		}
		// Add computed _displayLabel field for each item
		const fields = this._bindLabelFields as string[];
		return items.map(item => ({
			...item,
			_displayLabel: fields
				.map((field: string) => item[field])
				.filter((v: any) => v != null && v !== "")
				.join(" ")
		}));
	}
	public selectedItem(event: any) {
		this.selectItem.emit(event);
	}
	value: any;
	onChange = (value: any) => {};
	onTouched = () => {};

	writeValue(value: any): void {
		this.value = value;
		// Répercute la valeur sur le formControl interne (mode ngModel) sans re-déclencher onChange.
		if (this.formControl && this.formControl.value !== value) {
			this.formControl.setValue(value, { emitEvent: false });
		}
		if (this.items) this.ensureSelectedLoaded();
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	onFocus() {
		this.restApiService.get(this.collection, { filters: this.effectiveFilter, populate: this.populate, pagination: { page: 0, pageSize: environment.pageSizeAll }, jwt: "" }).subscribe((response) => {
			let data: any[];
			if (this.collection === Collection.UTILISATEUR) {
				data = response;
			} else {
				data = response.data;
			}
			this.items = this.processItems(data);
		});
	}
    
	onClear(event) {
		this.clear.emit(event);
	}
}
